import React, { useRef, useEffect } from 'react';
import * as THREE from 'three';
import { createNoise3D } from 'simplex-noise';

const vertexShader = `
  varying float vElevation;
  void main() {
    vElevation = position.z;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const fragmentShader = `
  varying float vElevation;
  uniform vec3 colorLow;
  uniform vec3 colorHigh;
  
  void main() {
    float alpha = 0.5;
    float elevationFactor = pow(clamp(vElevation * 3.0, 0.0, 1.0), 0.25); // Adjusted exponent
    vec3 color = mix(colorLow, colorHigh, elevationFactor);
    gl_FragColor = vec4(color, alpha);
  }
`;

const BackgroundAnimation = () => {
    const canvasRef = useRef(null);
    const sceneRef = useRef(null);
    const cameraRef = useRef(null);
    const rendererRef = useRef(null);
    const meshRef = useRef(null);
    const frameRef = useRef(null);
    const noiseRef = useRef(createNoise3D());

    useEffect(() => {
        const scene = new THREE.Scene();
        sceneRef.current = scene;

        const camera = new THREE.PerspectiveCamera(60, window.innerWidth / window.innerHeight, 0.1, 1000);
        camera.position.z = 1.2;
        cameraRef.current = camera;

        const renderer = new THREE.WebGLRenderer({
            canvas: canvasRef.current,
            alpha: true,
            antialias: true,
            precision: 'highp'
        });
        renderer.setSize(window.innerWidth, window.innerHeight);
        renderer.setPixelRatio(window.devicePixelRatio);
        rendererRef.current = renderer;

        const geometry = new THREE.PlaneGeometry(12, 6, 75, 75); // Wider view, fewer vertical lines

        const material = new THREE.ShaderMaterial({
            uniforms: {
                colorLow: { value: new THREE.Color(0x000000) }, // Black for low values
                colorHigh: { value: new THREE.Color(0xffd700) }  // Gold for high values
            },
            vertexShader,
            fragmentShader,
            wireframe: true,
            transparent: true
        });


        const mesh = new THREE.Mesh(geometry, material);
        mesh.rotation.x = -0.4;
        mesh.position.y = 0;
        scene.add(mesh);
        meshRef.current = mesh;

        camera.position.z = 5.0;

        let time = 0;
        const animate = () => {
            frameRef.current = requestAnimationFrame(animate);
            time += 0.00013;

            const positions = geometry.attributes.position;
            const amp = 0.8;    // Slightly higher amplitude
            const freq = 0.35;  // Lower frequency for smoother waves

            for (let i = 0; i < positions.count; i++) {
                const x = positions.getX(i);
                const y = positions.getY(i);

                const noise1 = noiseRef.current(x * freq, y * freq, time) * amp;
                const noise2 = noiseRef.current(x * freq * 2, y * freq * 2, time * 0.5) * (amp * 0.3);

                const z = noise1 + noise2;
                positions.setZ(i, z);
            }

            positions.needsUpdate = true;
            renderer.render(scene, camera);
        };

        animate();

        const handleResize = () => {
            camera.aspect = window.innerWidth / window.innerHeight;
            camera.updateProjectionMatrix();
            renderer.setSize(window.innerWidth, window.innerHeight);
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            cancelAnimationFrame(frameRef.current);
            if (meshRef.current) {
                meshRef.current.geometry.dispose();
                meshRef.current.material.dispose();
            }
            if (rendererRef.current) {
                rendererRef.current.dispose();
            }
        };
    }, []);

    return (
        <canvas
            ref={canvasRef}
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                zIndex: -1,
                opacity: 0.6
            }}
        />
    );
};

export default BackgroundAnimation;